import React, { useEffect, useState } from "react";
import { ArrowRight, LoaderCircle, LogIn, Sprout, UserRoundPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import RegisterWizard from "@/features/auth/RegisterWizard";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { PLATFORM_NAME } from "@/lib/polenica";

function PortalTab({ active, label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
        active ? "bg-primary text-primaryForeground shadow-sm" : "bg-white text-textMain hover:bg-[#F3F4EE]"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function LoginPanel({ onSubmit, onForgotPassword, onResetPassword, loading }) {
  const resetParams = new URLSearchParams(window.location.search);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mode, setMode] = useState(
    resetParams.get("reset") === "1" && resetParams.get("token") ? "reset" : "login",
  );
  const [message, setMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    try {
      await onSubmit(email, password);
    } catch (submitError) {
      const detail = submitError.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No fue posible iniciar sesión.");
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setActionLoading(true);
    try {
      const response = await onForgotPassword(email);
      setMessage(response.message || "Si el correo existe, enviaremos instrucciones.");
    } catch (submitError) {
      const detail = submitError.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No fue posible enviar las instrucciones.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setActionLoading(true);
    try {
      const response = await onResetPassword({
        token: resetParams.get("token"),
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setMessage(response.message || "Contraseña actualizada. Ya puede iniciar sesión.");
      setMode("login");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (submitError) {
      const detail = submitError.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "No fue posible restablecer la contraseña.");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="rounded-[30px] border border-primary/10 bg-white p-6 shadow-[0_24px_60px_rgba(59,90,63,0.08)]">
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-primary/70">Entrar a Polénica</p>
        <h2 className="mt-2 font-heading text-3xl font-semibold text-primary">Inicie sesión en su cuenta</h2>
        <p className="mt-2 text-sm leading-6 text-mutedForeground">
          Ingrese para gestionar sus aportes, publicar contenidos y seguir polinizando la agroecología urbana en
          Medellín.
        </p>
      </div>

      {error ? (
        <div className="mb-5 rounded-2xl border border-[#D68B72]/40 bg-[#FFF5F1] px-4 py-3 text-sm text-[#8A3D22]">
          {error}
        </div>
      ) : null}

      {message ? (
        <div className="mb-5 rounded-2xl border border-primary/20 bg-[#EFF7E7] px-4 py-3 text-sm text-primary">
          {message}
        </div>
      ) : null}

      {mode === "forgot" ? (
        <form className="space-y-4" onSubmit={handleForgotPassword}>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-primary">Correo registrado</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/40"
              placeholder="correo@ejemplo.com"
            />
          </label>
          <button
            type="submit"
            disabled={actionLoading}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-semibold text-primaryForeground shadow-[0_16px_28px_rgba(59,90,63,0.18)] disabled:cursor-not-allowed disabled:opacity-65"
          >
            {actionLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            Enviar instrucciones
          </button>
          <button type="button" className="text-sm font-semibold text-primary" onClick={() => setMode("login")}>
            Volver al inicio de sesión
          </button>
        </form>
      ) : null}

      {mode === "reset" ? (
        <form className="space-y-4" onSubmit={handleResetPassword}>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-primary">Nueva contraseña</span>
            <input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              className="h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/40"
              placeholder="Mínimo 8 caracteres, una mayúscula y un número"
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-primary">Confirmar contraseña</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/40"
              placeholder="Repita la contraseña"
            />
          </label>
          <button
            type="submit"
            disabled={actionLoading}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-semibold text-primaryForeground shadow-[0_16px_28px_rgba(59,90,63,0.18)] disabled:cursor-not-allowed disabled:opacity-65"
          >
            {actionLoading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
            Restablecer contraseña
          </button>
        </form>
      ) : null}

      {mode === "login" ? (
      <form className="space-y-4" onSubmit={handleSubmit}>
        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-primary">Correo</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/40"
            placeholder="correo@ejemplo.com"
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-primary">Contraseña</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="h-12 w-full rounded-2xl border border-border bg-[#FCFCF9] px-4 outline-none transition focus:border-primary/40"
            placeholder="********"
          />
        </label>

        <button
          type="submit"
          disabled={loading}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-semibold text-primaryForeground shadow-[0_16px_28px_rgba(59,90,63,0.18)] disabled:cursor-not-allowed disabled:opacity-65"
        >
          {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
          Entrar
        </button>
        <button
          type="button"
          className="text-sm font-semibold text-primary underline decoration-primary/30 underline-offset-4"
          onClick={() => {
            setError("");
            setMessage("");
            setMode("forgot");
          }}
        >
          Olvidé mi contraseña
        </button>
      </form>
      ) : null}
    </div>
  );
}

export default function AuthPortal({ mode = "login" }) {
  const navigate = useNavigate();
  const { user, login, register, accessAsGuest, loading: authLoading } = useAuth();
  const editorMode = new URLSearchParams(window.location.search).get("contentEditor") === "1";
  const [activeMode, setActiveMode] = useState(mode);
  const [loginLoading, setLoginLoading] = useState(false);
  const requestedPath = new URLSearchParams(window.location.search).get("next");
  const postLoginPath =
    requestedPath && requestedPath.startsWith("/") && !requestedPath.startsWith("//")
      ? requestedPath
      : "/inicio";

  useEffect(() => {
    setActiveMode(mode);
  }, [mode]);

  useEffect(() => {
    if (!editorMode && !authLoading && user) {
      navigate(postLoginPath, { replace: true });
    }
  }, [authLoading, editorMode, navigate, postLoginPath, user]);

  const handleLogin = async (email, password) => {
    setLoginLoading(true);
    try {
      await login(email, password);
      navigate(postLoginPath, { replace: true });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGuestAccess = () => {
    accessAsGuest();
    navigate("/inicio", { replace: true });
  };

  const handleForgotPassword = async (email) => {
    const response = await api.post("/auth/forgot-password", { email });
    return response.data;
  };

  const handleResetPassword = async (payload) => {
    const response = await api.post("/auth/reset-password", payload);
    return response.data;
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(181,212,158,0.24),_transparent_26%),linear-gradient(180deg,_#F6F5F0_0%,_#EEF3E7_100%)]">
      <div className="mx-auto grid min-h-screen max-w-[1500px] gap-10 px-5 py-6 lg:grid-cols-[1.08fr_0.92fr] lg:px-8">
        <section
          className="relative overflow-hidden rounded-[36px] border border-white/35 bg-[#314739] bg-cover bg-center px-6 py-8 text-white shadow-[0_24px_90px_rgba(28,45,33,0.26)] sm:px-8 sm:py-10"
          style={{
            backgroundImage:
              "linear-gradient(145deg,rgba(18,33,24,0.82),rgba(37,61,44,0.7),rgba(203,180,126,0.22)), url('/images/portada.jpg')",
          }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(229,240,202,0.16),transparent_26%),radial-gradient(circle_at_78%_18%,rgba(255,255,255,0.16),transparent_22%),linear-gradient(180deg,rgba(255,255,255,0.04),transparent_42%)]" />
          <div className="relative z-10 flex h-full flex-col justify-between gap-8">
            <div>
              <div className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/10 px-4 py-2 backdrop-blur-md">
                <Sprout className="h-5 w-5" />
                <span className="text-sm font-semibold tracking-[0.12em]">{PLATFORM_NAME}</span>
              </div>

              <p className="mt-10 text-[11px] font-semibold uppercase tracking-[0.32em] text-white/78">
                Ingreso a la red
              </p>
              <h1 className="mt-4 max-w-2xl font-heading text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
                Sea polénico: conecte, comparta y polinice Medellín.
              </h1>

              <p className="mt-5 max-w-2xl text-base leading-8 text-white/84">
                Inicie sesión o cree una cuenta para sumar iniciativas al mapa, publicar productos y servicios,
                compartir saberes y activar encuentros que fortalecen la agroecología urbana.
              </p>
            </div>

            <div className="max-w-xl rounded-[28px] border border-white/12 bg-white/10 p-5 backdrop-blur-md">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/76">Acceso flexible</p>
              <p className="mt-3 text-sm leading-7 text-white/84">
                Puede entrar con cuenta o recorrer primero la plataforma en modo invitado para explorar mapa, agenda,
                directorio y saberes vivos.
              </p>
            </div>
          </div>
        </section>

        <section className="flex flex-col justify-center gap-5">
          <div className="flex flex-wrap items-center gap-3 rounded-full border border-primary/10 bg-white/80 p-2 shadow-sm backdrop-blur-md">
            <PortalTab
              active={activeMode === "login"}
              label="Iniciar sesión"
              icon={LogIn}
              onClick={() => setActiveMode("login")}
            />
            <PortalTab
              active={activeMode === "register"}
              label="Registrarse"
              icon={UserRoundPlus}
              onClick={() => setActiveMode("register")}
            />
            <button
              type="button"
              onClick={handleGuestAccess}
              className="ml-auto inline-flex items-center gap-2 rounded-full border border-primary/12 bg-[#F5F8F1] px-4 py-2 text-sm font-semibold text-primary transition hover:bg-[#EEF4E7]"
            >
              Entrar como invitado
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {activeMode === "login" ? (
            <LoginPanel
              onSubmit={handleLogin}
              onForgotPassword={handleForgotPassword}
              onResetPassword={handleResetPassword}
              loading={loginLoading}
            />
          ) : (
            <RegisterWizard
              register={register}
              onSuccess={() => {
                navigate("/inicio", { replace: true });
              }}
            />
          )}
        </section>
      </div>
    </div>
  );
}
